import SpmConstants from 'sg-spm-constants';
import _ from 'lodash';

const VEHICLE_CHANGES = {
    REGISTRATION: 'registrationNumber',
    VEHICLE: 'vehicle',
    MILEAGE: 'mileage',
    VEHICLE_USE: 'vehicleUse',
    PARKING_TYPE: 'vehicleParkingType',
    ADDED_MODIFICATION: 'addedModification',
    REMOVED_MODIFICATION: 'removedModification'
};

const DRIVER_CHANGES = {
    ADDED_DRIVER: 'addedDriver',
    REMOVED_DRIVER: 'removedDriver'
};

const COVERAGES = {
    ADDED: 'coverageAdded',
    REMOVED: 'coverageRemoved',
    MODIFIED_VOLUNTARY_AD: 'voluntaryExcess',
    MODIFIED_COMPULSORY_AD: 'compulsoryExcess',
    MODIFIED_BREAKDOWN_LEVEL: 'breakdownCoverLevel',
    MODIFIED_FIRE_THEFT: 'fireAndTheftExcess',
    CURRENT_PNCD_PRICE: 'pncdPrice',
};

const EXCESS_KEYS = [
    COVERAGES.MODIFIED_COMPULSORY_AD,
    COVERAGES.MODIFIED_VOLUNTARY_AD
];

let endorsementChanges;
let previousModel;

function pushChanges(typeOfChange, changedObject, createArray) {
    if (createArray) {
        if (endorsementChanges[typeOfChange]) {
            endorsementChanges[typeOfChange].push(changedObject);
        } else {
            endorsementChanges[typeOfChange] = [changedObject];
        }
    } else {
        endorsementChanges[typeOfChange] = changedObject;
    }
}

function addModificationChangesIfPresent(changes, previousVehicle, currentVehicle, getValue) {
    currentVehicle.vehicleModifications.forEach((currentMod) => {
        if (currentMod.modificationCategory && currentMod.modificationType) {
            const matchingModification = previousVehicle.vehicleModifications.find((prevModification) => {
                return prevModification.modificationCategory === currentMod.modificationCategory.value.code
                    && prevModification.modificationType === currentMod.modificationType.value.code;
            });
            if (!matchingModification) {
                pushChanges(VEHICLE_CHANGES.ADDED_MODIFICATION, getValue(currentMod.modificationType.value), true);
            }
        }
    });
    previousVehicle.vehicleModifications.forEach((prevMod) => {
        const matchingModification = currentVehicle.vehicleModifications.value.find((currentModification) => {
            return currentModification.modificationCategory === prevMod.modificationCategory
                && currentModification.modificationType === prevMod.modificationType;
        });
        if (!matchingModification) {
            pushChanges(VEHICLE_CHANGES.REMOVED_MODIFICATION,
                getValue({
                    typelist: {
                        name: 'VehicleModification_Ext'
                    },
                    code: prevMod.modificationType
                }), true);
        }
    });
}

function updateCoverageChangeFlag(modelVM) {
    if (modelVM.lobData.privateMotor.selectedChanges.value) {
        _.set(modelVM, 'lobData.privateMotor.selectedChanges.value.hasCoveragesChanges', true);
    }
}

function setEditedCoverage(previousCoverage, currentCoverage, modelVM) {
    switch (currentCoverage.patternCode) {
        case SpmConstants.BASIC_MOTOR_COVER_CODE: {
            const prevFTExcess = previousCoverage.terms.find((term) => term.patternCode === SpmConstants.FIRE_THEFT_EXCESS_CODE);
            const currFTExcess = currentCoverage.terms.find((term) => term.patternCode === SpmConstants.FIRE_THEFT_EXCESS_CODE);
            if (prevFTExcess && currFTExcess && prevFTExcess.chosenTerm !== currFTExcess.chosenTerm) {
                pushChanges(COVERAGES.MODIFIED_FIRE_THEFT, currFTExcess.chosenTermValue);
                updateCoverageChangeFlag(modelVM);
            }
            const prevVolAdExcess = previousCoverage.terms.find((term) => term.patternCode === SpmConstants.VOLUNTARY_AD_EXCESS_CODE);
            const currVolAdExcess = currentCoverage.terms.find((term) => term.patternCode === SpmConstants.VOLUNTARY_AD_EXCESS_CODE);
            if (prevVolAdExcess && currVolAdExcess && prevVolAdExcess.chosenTerm !== currVolAdExcess.chosenTerm) {
                pushChanges(COVERAGES.MODIFIED_VOLUNTARY_AD, currVolAdExcess.chosenTermValue);
                updateCoverageChangeFlag(modelVM);
            }
            const prevCompAdExcess = previousCoverage.terms.find((term) => term.patternCode === SpmConstants.COMPULSORY_AD_EXCESS_CODE);
            const currCompAdExcess = currentCoverage.terms.find((term) => term.patternCode === SpmConstants.COMPULSORY_AD_EXCESS_CODE);
            if (prevCompAdExcess && currCompAdExcess && prevCompAdExcess.chosenTerm !== currCompAdExcess.chosenTerm) {
                pushChanges(COVERAGES.MODIFIED_COMPULSORY_AD, currCompAdExcess.chosenTermValue);
                updateCoverageChangeFlag(modelVM);
            }
            break;
        }
        case SpmConstants.BREAKDOWN_CODE: {
            const prevBreakdownLevelTerm = previousCoverage.terms.find((term) => term.patternCode === SpmConstants.BREAKDOWN_LEVEL_CODE);
            const currBreakdownLevelTerm = currentCoverage.terms.find((term) => term.patternCode === SpmConstants.BREAKDOWN_LEVEL_CODE);
            if (prevBreakdownLevelTerm && currBreakdownLevelTerm && prevBreakdownLevelTerm.chosenTerm !== currBreakdownLevelTerm.chosenTerm) {
                pushChanges(COVERAGES.MODIFIED_BREAKDOWN_LEVEL, currBreakdownLevelTerm.chosenTermValue);
            }
            break;
        }
        case SpmConstants.NCD_PROTECTION_CODE: {
            if (previousCoverage.amount && currentCoverage.amount && previousCoverage.amount.amount !== currentCoverage.amount.amount) {
                pushChanges(COVERAGES.CURRENT_PNCD_PRICE, currentCoverage.amount.amount.toFixed(2));
                updateCoverageChangeFlag(modelVM);
            }
            break;
        }
        default:
    }
}

function addCoverageChanges(modelVM) {
    const { coverages } = modelVM.value.lobData.privateMotor.offerings[0].coverages.vehicleCoverages[0];
    const previousCoverages = previousModel.lobData.privateMotor.offerings[0].coverages.vehicleCoverages[0].coverages;
    coverages.filter((cov) => cov.selected).forEach((currentSelectedCoverage) => {
        const matchingCoverage = previousCoverages.find((el) => el.publicID === currentSelectedCoverage.publicID);
        if (!matchingCoverage || !matchingCoverage.selected) {
            pushChanges(COVERAGES.ADDED, currentSelectedCoverage, true);
        } else {
            setEditedCoverage(matchingCoverage, currentSelectedCoverage, modelVM);
        }
    });
    previousCoverages.filter((cov) => cov.selected).forEach((previouslySelectedCoverage) => {
        const matchingCoverage = coverages.find((el) => el.publicID === previouslySelectedCoverage.publicID && el.selected);
        if (!matchingCoverage) {
            pushChanges(COVERAGES.REMOVED, previouslySelectedCoverage, true);
        }
    });
}

function addDriverChanges(modelVM) {
    const { spmDrivers } = modelVM.lobData.privateMotor.coverables;
    const previousDrivers = previousModel.lobData.privateMotor.coverables.spmDrivers;
    let changedDriver = previousDrivers.find((driver) => !spmDrivers.value.some((obj) => obj.publicID === driver.publicID));
    if (changedDriver) {
        pushChanges(DRIVER_CHANGES.REMOVED_DRIVER, changedDriver);
    } else {
        changedDriver = spmDrivers.value.find((driver) => !previousDrivers.some((obj) => obj.publicID === driver.publicID));
        pushChanges(DRIVER_CHANGES.ADDED_DRIVER, changedDriver);
    }
}

function addVehicleChanges(modelVM, getValue) {
    const { spmVehicle } = modelVM.lobData.privateMotor.coverables;
    const previousVehicle = previousModel.lobData.privateMotor.coverables.spmVehicle;

    if (previousVehicle.model !== spmVehicle.value.model) {
        pushChanges(VEHICLE_CHANGES.VEHICLE,
            { registration: spmVehicle.value.registrationNumber, make: spmVehicle.value.make, model: spmVehicle.value.model });
    }
    if (previousVehicle.registrationNumber !== spmVehicle.value.registrationNumber) {
        if (previousVehicle.model === spmVehicle.value.model) {
            pushChanges(VEHICLE_CHANGES.REGISTRATION, spmVehicle.value.registrationNumber);
        }
    }
    if (previousVehicle.mileage !== spmVehicle.value.mileage) {
        pushChanges(VEHICLE_CHANGES.MILEAGE, spmVehicle.mileage.value);
    }
    if (previousVehicle.vehicleUse !== spmVehicle.value.vehicleUse) {
        pushChanges(VEHICLE_CHANGES.VEHICLE_USE, spmVehicle.vehicleUse.value);
    }
    if (previousVehicle.vehicleParkingType !== spmVehicle.value.vehicleParkingType) {
        pushChanges(VEHICLE_CHANGES.PARKING_TYPE, spmVehicle.vehicleParkingType.value);
    }
    addModificationChangesIfPresent(endorsementChanges, previousVehicle, spmVehicle, getValue);
}

function getMotorChanges(modelVM, getValue, isRenewal) {
    endorsementChanges = {};
    if (isRenewal || _.get(modelVM, 'lobData.privateMotor.selectedChanges.value.hasVehicleChanges')) {
        addVehicleChanges(modelVM, getValue);
    }
    if (_.get(modelVM, 'lobData.privateMotor.selectedChanges.value.hasDriverChanges')) {
        addDriverChanges(modelVM);
        addVehicleChanges(modelVM, getValue);
    }
    addCoverageChanges(modelVM);
    return endorsementChanges;
}

function getPreviousCost() {
    return previousModel.totalCost;
}

function hasVehicleChanges(changes) {
    return _.some(Object.keys(changes), (changeKey) => Object.values(VEHICLE_CHANGES).includes(changeKey));
}

function hasCoverageChanges(changes) {
    return _.some(Object.keys(changes), (changeKey) => Object.values(COVERAGES).includes(changeKey));
}

function hasExcessChanges(changes) {
    return _.some(Object.keys(changes), (changeKey) => EXCESS_KEYS.includes(changeKey));
}

const savePreviousModel = (modelVM) => {
    previousModel = modelVM;
};

export default {
    savePreviousModel,
    hasVehicleChanges,
    hasCoverageChanges,
    hasExcessChanges,
    getPreviousCost,
    getMotorChanges
};
